import React, { useMemo, useCallback } from 'react';
import { Splitter, ConfigProvider } from 'antd';
import * as Icons from '@ant-design/icons';
import type { SplitterProps as AntSplitterProps } from 'antd';
import { useAndStore } from '../../base/store/useAndStore';
import { createCommand } from '../../base/actionParser';
import type { BaseComponentProps } from '../../types/common';

/**
 * Splitter Panel 配置接口
 */
export interface SplitterPanelConfig {
  /** 面板唯一标识 */
  key: string;
  /** 默认尺寸 (数字或百分比字符串) */
  defaultSize?: number | string;
  /** 最小尺寸 */
  min?: number | string;
  /** 最大尺寸 */
  max?: number | string;
  /** 是否可折叠 */
  collapsible?: boolean | { start?: boolean; end?: boolean; showCollapsibleIcon?: boolean | 'auto' };
  /** 是否可调整大小 */
  resizable?: boolean;
}

/**
 * Semantic DOM 样式配置
 */
export interface SplitterSemanticStyles {
  root?: React.CSSProperties;
  panel?: React.CSSProperties | ((info: { index: number }) => React.CSSProperties);
  dragger?: React.CSSProperties;
}

/**
 * AndSplitter 组件属性
 */
export interface AndSplitterProps extends Omit<AntSplitterProps, 'onResize' | 'onResizeEnd' | 'collapsibleIcon' | 'draggerIcon'>, BaseComponentProps {
  /** 面板配置列表 */
  panels?: SplitterPanelConfig[] | string;
  /** 布局方向 (orientation in Ant Design 6.x) */
  orientation?: 'horizontal' | 'vertical';
  /** 延迟渲染模式 */
  lazy?: boolean;
  /** 拖拽图标 (图标名称字符串) */
  draggerIcon?: string;
  /** 折叠图标 (start/end 图标名称字符串) */
  collapsibleIcon?: { start?: string; end?: string } | string;
  /** 调整大小时的动作 */
  resizeAction?: string;
  /** 调整大小结束时的动作 */
  resizeEndAction?: string;
  /** 自定义 onResize */
  onResize?: (sizes: number[]) => void;
  /** 自定义 onResizeEnd */
  onResizeEnd?: (sizes: number[]) => void;
  /** Design Token */
  token?: Record<string, any> | string;
  /** 子组件 (用于运行时渲染) */
  children?: React.ReactNode;
  /** 面板内容渲染函数 (用于编辑器模式) */
  renderPanelContent?: (panelKey: string, index: number) => React.ReactNode;
}

/**
 * 根据图标名称创建图标组件
 */
const createIconFromName = (iconName: string | undefined): React.ReactNode => {
  if (!iconName) return undefined;
  const IconComponent = (Icons as any)[iconName];
  if (IconComponent) {
    return React.createElement(IconComponent);
  }
  return undefined;
};

/**
 * 解析 JSON 字符串
 */
const parseJson = <T,>(value: T | string | undefined, fallback: T): T => {
  if (value === undefined || value === null) return fallback;
  if (typeof value === 'string') {
    try {
      const trimmed = value.trim();
      if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
        return JSON.parse(trimmed);
      }
    } catch {
      return fallback;
    }
  }
  return value as T;
};

/**
 * AndSplitter 组件
 * 基于 Ant Design Splitter，支持可视化配置
 */
export const AndSplitter: React.FC<AndSplitterProps> = ({
  id,
  sessionId,
  develop,
  panels: propPanels,
  orientation = 'horizontal',
  lazy = false,
  draggerIcon,
  collapsibleIcon,
  resizeAction,
  resizeEndAction,
  onResize,
  onResizeEnd,
  styles,
  token: propToken,
  children,
  renderPanelContent,
  ...restProps
}) => {
  const addCommand = useAndStore((state) => state.addCommand);

  // 解析面板配置
  const panels = useMemo(
    () => parseJson(propPanels, [] as SplitterPanelConfig[]),
    [propPanels]
  );

  // 解析 token
  const token = useMemo(() => parseJson(propToken, {}), [propToken]);

  // 解析图标
  const resolvedDraggerIcon = useMemo(() => createIconFromName(draggerIcon), [draggerIcon]);

  const resolvedCollapsibleIcon = useMemo(() => {
    if (!collapsibleIcon) return undefined;
    if (typeof collapsibleIcon === 'string') {
       // 如果是字符串，尝试解析为 JSON 或直接作为图标名 (暂不支持单字符串直接作为 icon，需明确 structure)
       //为了兼容性，如果传入字符串，假设它是 JSON
       const parsed = parseJson(collapsibleIcon, null);
       if (parsed && typeof parsed === 'object') {
          return {
            start: createIconFromName((parsed as any).start),
            end: createIconFromName((parsed as any).end),
          };
       }
       return undefined;
    }
    return {
      start: createIconFromName(collapsibleIcon.start),
      end: createIconFromName(collapsibleIcon.end),
    };
  }, [collapsibleIcon]);

  // 处理调整大小事件
  const handleResize = useCallback(
    (sizes: number[]) => {
      if (onResize) {
        onResize(sizes);
        return;
      }
      if (resizeAction && sessionId) {
        addCommand(sessionId, createCommand(resizeAction, { sizes }));
      }
    },
    [onResize, resizeAction, sessionId, addCommand]
  );

  // 处理调整大小结束事件
  const handleResizeEnd = useCallback(
    (sizes: number[]) => {
      if (onResizeEnd) {
        onResizeEnd(sizes);
        return;
      }
      if (resizeEndAction && sessionId) {
        addCommand(sessionId, createCommand(resizeEndAction, { sizes }));
      }
    },
    [onResizeEnd, resizeEndAction, sessionId, addCommand]
  );

  // 渲染面板内容
  const renderPanels = () => {
    // 如果有 children (运行时模式)，使用 children
    if (children && React.Children.count(children) > 0) {
      return React.Children.map(children, (child, index) => {
        const panelConfig = panels[index] || {};
        return (
          <Splitter.Panel
            key={panelConfig.key || index}
            defaultSize={panelConfig.defaultSize}
            min={panelConfig.min}
            max={panelConfig.max}
            collapsible={panelConfig.collapsible}
            resizable={panelConfig.resizable !== false}
          >
            {child}
          </Splitter.Panel>
        );
      });
    }

    // 根据面板配置渲染
    return panels.map((panel, index) => (
      <Splitter.Panel
        key={panel.key || index}
        defaultSize={panel.defaultSize || undefined}
        min={panel.min || undefined}
        max={panel.max || undefined}
        collapsible={panel.collapsible}
        resizable={panel.resizable !== false}
      >
        {renderPanelContent ? (
          renderPanelContent(panel.key, index)
        ) : (
          <div
            style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#999',
              fontSize: 14,
            }}
          >
            Panel {index + 1}
          </div>
        )}
      </Splitter.Panel>
    ));
  };

  // 创建 Splitter 元素
  // 注意: 使用 layout 解构避免 restProps 中残留的 deprecated layout 属性传入
  const { layout: _ignoredLayout, ...cleanRestProps } = restProps as any;
  const splitterElement = (
    <div style={{ width: '100%', height: '100%' }}>
      <Splitter
        key={orientation} // Force re-render inside this wrapper to avoid blank screen and GrapesJS root conflict
        {...cleanRestProps}
        orientation={orientation}
        lazy={lazy}
        onResize={handleResize}
        onResizeEnd={handleResizeEnd}
        style={{ height: '100%', width: '100%', ...cleanRestProps.style }}
        styles={styles}
        draggerIcon={resolvedDraggerIcon}
        collapsibleIcon={resolvedCollapsibleIcon}
      >
        {renderPanels()}
      </Splitter>
    </div>
  );

  // 应用 Design Token
  if (token && Object.keys(token).length > 0) {
    return (
      <ConfigProvider theme={{ components: { Splitter: token } }}>
        {splitterElement}
      </ConfigProvider>
    );
  }

  return splitterElement;
};


/**
 * AndSplitterPanel 组件
 * 仅用于作为 AndSplitter 的子组件容器，实际的分隔面板逻辑由 AndSplitter 处理
 * 此组件主要用于编辑器识别和属性传递
 */
export const AndSplitterPanel: React.FC<SplitterPanelConfig & { children?: React.ReactNode; className?: string; style?: React.CSSProperties; [key: string]: any }> = ({
  children,
  className,
  style,
  // 过滤掉 SplitterPanelConfig 中的属性，避免传递给 div 导致 React 警告
  key,
  defaultSize,
  min,
  max,
  collapsible,
  resizable,
  sessionId,
  ...restProps
}) => {
  return (
    <div
      className={className}
      style={{ display: 'contents', width: '100%', height: '100%', ...style }}
      {...restProps}
    >
      {children}
    </div>
  );
};

export default AndSplitter;
